/* ============================================================
   Cosmic Studio — Cart JS  |  cs-cart.js
   Responsibilities:
   1. Upsell "Add" button → POST to cart/add.js → trigger
      Horizon's native cart section refresh via SRA.
   2. Auto-gift → watch for cart changes, add/remove free
      gift variants when total crosses configured thresholds.

   We do NOT touch Horizon's drawer open/close/render.
   Horizon's SRA handles re-rendering after every cart change,
   including our milestone bar and upsell strip.
   ============================================================ */

(function () {
  'use strict';

  const ROOT  = window.Shopify?.routes?.root || '/';
  const cfg   = window.CSCartConfig || {};

  /* ─── helpers ─────────────────────────────────────────── */

  async function fetchCart() {
    const r = await fetch(ROOT + 'cart.js');
    return r.json();
  }

  /**
   * Trigger Horizon's Section Rendering API refresh for the
   * cart-drawer-section. This re-renders the drawer HTML
   * (including our milestone bar + upsells) with fresh Liquid.
   */
  async function refreshCart() {
    try {
      // Horizon listens for this event to refresh the cart
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      // Fallback: directly fetch and update via SRA if Horizon
      // doesn't respond to the event within 400ms
      await new Promise(resolve => setTimeout(resolve, 400));

      const res = await fetch('/?sections=cart-drawer-section');
      if (!res.ok) return;
      const data = await res.json();
      const html = data['cart-drawer-section'];
      if (!html) return;

      const parser = new DOMParser();
      const doc    = parser.parseFromString(html, 'text/html');

      /* Update the cart inner div (what Horizon's SRA targets) */
      const newInner = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
      const oldInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
      if (newInner && oldInner) {
        oldInner.innerHTML = newInner.innerHTML;
      }
    } catch (err) {
      /* Silent fail — Horizon's own event-based refresh is primary */
    }
  }

  /* ─── upsell add-to-cart ──────────────────────────────── */

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cs-upsell]');
    if (!btn) return;

    const variantId = parseInt(btn.dataset.csUpsell, 10);
    if (!variantId) return;

    const label      = btn.textContent;
    btn.disabled     = true;
    btn.textContent  = '…';

    try {
      const res = await fetch(ROOT + 'cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (res.ok) {
        await refreshCart();
      } else {
        btn.disabled    = false;
        btn.textContent = label;
      }
    } catch {
      btn.disabled    = false;
      btn.textContent = label;
    }
  });

  /* ─── auto-gift ───────────────────────────────────────── */

  let giftBusy       = false;
  let lastCartToken  = null;
  let giftDebounce   = null;

  function scheduleGiftCheck() {
    clearTimeout(giftDebounce);
    giftDebounce = setTimeout(runGiftCheck, 600);
  }

  async function runGiftCheck() {
    if (giftBusy) return;
    const gifts = (cfg.gifts || []).filter(g => g.variantId && g.variantId !== 0);
    if (!gifts.length) return;

    giftBusy = true;
    try {
      const cart = await fetchCart();

      /* Skip if cart hasn't changed since last check */
      if (cart.token === lastCartToken) return;

      let changed = false;

      for (const gift of gifts) {
        const inCart    = cart.items.find(i =>
          i.variant_id === gift.variantId &&
          i.properties?._cs_gift === 'true'
        );
        const shouldHave = cart.total_price >= gift.threshold;

        if (shouldHave && !inCart) {
          /* Add gift — variant price must be ₹0 in Shopify admin */
          await fetch(ROOT + 'cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({
              id:         gift.variantId,
              quantity:   1,
              properties: { _cs_gift: 'true' },
            }),
          });
          changed = true;

        } else if (!shouldHave && inCart) {
          /* Remove gift */
          await fetch(ROOT + 'cart/change.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ id: inCart.key, quantity: 0 }),
          });
          changed = true;
        }
      }

      /* Record token AFTER our changes so next observer skip fires */
      const updated     = await fetchCart();
      lastCartToken     = updated.token;

      if (changed) {
        await refreshCart();
      }
    } finally {
      giftBusy = false;
    }
  }

  /* ─── observe cart for changes ────────────────────────── */

  function observe() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    /* Watch for Horizon re-rendering the cart inner div */
    const mo = new MutationObserver((mutations) => {
      const hasContent = mutations.some(m =>
        m.type === 'childList' && m.addedNodes.length > 0
      );
      if (hasContent) scheduleGiftCheck();
    });

    mo.observe(drawer, { childList: true, subtree: true });

    /* Also listen for Horizon's cart events */
    document.addEventListener('cart:updated', scheduleGiftCheck);
    document.addEventListener('cart:change',  scheduleGiftCheck);
  }

  /* ─── boot ────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe, { once: true });
  } else {
    observe();
  }

})();